import { AutocompleteChangeReason, AutocompleteInputChangeReason, BaseTextFieldProps } from '@mui/material';
import { useState } from 'react';
import styled from 'styled-components';
import { DeprecatedLabeledSelect } from '../../deprecated/select/index';
import { strTruncate } from '../../utils/string';
import { TOP_COUNTRIES, useCountryCodeToNameJsonSplit, useStateCodeToNameJson } from '../country-list/index';
import { Divider } from '../divider/index';
import { MuiMenuItem } from '../mui-list/index';
import { Select } from '../select/index';
import { TextField } from '../text-field/index';
import { Typography } from '../typography/index';
import { AddressStateType } from './types';
import { addressStateToString } from './utils';

export interface AddressInputFormInterface {
	/** the address */
	addressState: AddressStateType | null;
	/** called when the address changes in the form */
	onChange: (newAddressState: AddressStateType) => void;

	/** called when the address changes in the form, but only after a field loses focus */
	onFieldBlur?: (newAddressState: AddressStateType) => void;

	/** predefined variants, applied before fieldProps etc.
	 * @default labelled
	 */
	variant?: 'labelled' | 'placeholders';

	/** size of fields.
	 * @default medium
	 */
	size?: BaseTextFieldProps['size'];

	/** props passed into all fields in the form*/
	fieldProps?: Record<string, unknown>;
	/**props passed into the all textFields in the form */
	textFieldProps?: Record<string, unknown>;
	/** props passed into all selects in the form */
	selectProps?: Record<string, unknown>;
}

// for consistent styling
function SelectDropdownHeading(props) {
	// const theme = useTheme();
	return <Typography align="center" variant="subtitle2" {...props} />;
}

/** for consistent styling */
const StyledLabeledSelect = props => (
	<DeprecatedLabeledSelect variant="outlined" className={`address-form-field select ${props.className}`} {...props} />
);

const GridWrapper = styled.div`
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: 1rem;
	box-sizing: inherit;
`;

export const addressFormVariants = {
	// exporting for storybook
	labelled: {
		fieldProps: { placeholder: undefined }, // turn off text field placeholders
		textFieldProps: {},
		selectProps: {}
	},
	placeholders: {
		fieldProps: { label: undefined }, // turn off text field labels
		textFieldProps: {},
		selectProps: {}
	}
};

const MAX_LEN_STATE = 50; // max length of state input

/** a form for editing an address.
 * does not keep any internal state of the address
 */
export function AddressInputForm({
	addressState,
	onChange,
	onFieldBlur,
	fieldProps,
	textFieldProps,
	selectProps,
	variant: variantName,
	size
}: AddressInputFormInterface) {
	// TODO this is not that efficient?
	/**variant name, default is labelled*/
	variantName = variantName ?? 'labelled';
	size = size ?? 'medium';
	const variant = addressFormVariants[variantName];
	const fieldPropsMerged = { ...variant?.fieldProps, ...fieldProps };
	const textFieldPropsMerged = { ...variant?.textFieldProps, ...textFieldProps };
	const selectPropsMerged = { ...variant?.selectProps, ...selectProps };

	const [stateInputVal, setStateInputVal] = useState(addressState?.state ?? '');

	const stateListJson = useStateCodeToNameJson(addressState?.country) ?? {};
	const { countryCodeToNameJsonAll, countryCodeToNameJsonTop, countryCodeToNameJsonOther, isLoading } = useCountryCodeToNameJsonSplit();

	function formattedOnChange(newAddressState: AddressStateType) {
		newAddressState.formatted = addressStateToString(newAddressState);
		onChange(newAddressState);
	}

	function formattedOnBlur(newAddressState: AddressStateType) {
		newAddressState.formatted = addressStateToString(newAddressState);
		onFieldBlur?.(newAddressState);
	}

	const states = Object.entries(stateListJson ?? {}).map(([key, value]) => ({ value: key, name: value }));
	const stateVal = states.find(s => s.value === addressState?.state) ?? null;

	function onStateInputChange(val: string, reason: AutocompleteInputChangeReason) {
		if (reason !== 'reset') {
			const lv = strTruncate(val, MAX_LEN_STATE);
			let newInputVal = lv;
			const newState = { ...addressState };
			const existingState = states.find(s => s.value === lv);
			if (existingState) {
				newState.state = existingState.value;
				newInputVal = existingState.name;
			} else {
				newState.state = lv;
				newInputVal = lv;
			}
			setStateInputVal(newInputVal ?? '');
			formattedOnChange(newState);
		}
	}

	function onStateChange(val: { value: string; name: string } | string | null, reason: AutocompleteChangeReason) {
		const newState = { ...addressState };
		let newInputVal;
		if (typeof val === 'string') {
			const lv = strTruncate(val, MAX_LEN_STATE);
			const existingState = states.find(s => s.value === lv);
			if (existingState) {
				newState.state = existingState.value;
				newInputVal = existingState.name;
			} else {
				newState.state = lv;
				newInputVal = lv;
			}
		} else {
			newState.state = val?.value;
			newInputVal = val?.name;
		}
		setStateInputVal(newInputVal ?? '');
		formattedOnChange(newState);
	}

	if (isLoading) {
		return <div>loading</div>;
	}

	return (
		<GridWrapper>
			<TextField
				size={size}
				style={{
					gridColumnEnd: 'span 2'
				}}
				label="Address"
				placeholder="Address"
				value={addressState?.address1 ?? addressState?.rawAddressInput}
				onChange={e => formattedOnChange({ ...addressState, address1: e.target.value })}
				onBlur={e => formattedOnBlur({ ...addressState, address1: e.target.value })}
				{...fieldPropsMerged}
				{...textFieldPropsMerged}
			/>
			<TextField
				size={size}
				style={{
					gridColumnEnd: 'span 2'
				}}
				label="Building name (optional)"
				placeholder="Building name (optional)"
				value={addressState?.building ?? ''}
				onChange={e => formattedOnChange({ ...addressState, building: e.target.value })}
				onBlur={e => formattedOnBlur({ ...addressState, building: e.target.value })}
				{...fieldPropsMerged}
				{...textFieldPropsMerged}
			/>
			<TextField
				size={size}
				label="Suburb"
				placeholder="Suburb"
				value={addressState?.locality ?? ''}
				onChange={e => formattedOnChange({ ...addressState, locality: e.target.value })}
				onBlur={e => formattedOnBlur({ ...addressState, locality: e.target.value })}
				{...fieldPropsMerged}
				{...textFieldPropsMerged}
			/>
			<TextField
				size={size}
				label="Postcode"
				placeholder="Postcode"
				value={addressState?.postcode ?? ''}
				onChange={e => formattedOnChange({ ...addressState, postcode: e.target.value })}
				onBlur={e => formattedOnBlur({ ...addressState, postcode: e.target.value })}
				{...fieldPropsMerged}
				{...textFieldPropsMerged}
			/>
			<Select<{ value: string; name: string }, false, false, true>
				label="State/Territory/Region"
				placeholder="State/Territory/Region"
				options={states}
				getOptionLabel={o => (typeof o === 'string' ? o : o.name)}
				inputValue={stateInputVal}
				value={stateVal}
				freeSolo
				isOptionEqualToValue={(option, value) => option.value === value.value}
				onInputChange={(_, val, reason) => onStateInputChange(val, reason)}
				onChange={(_, val, reason) => onStateChange(val, reason)}
				{...fieldPropsMerged}
				{...selectPropsMerged}
			/>
			<StyledLabeledSelect
				size={size}
				label="Country"
				placeholder="Country"
				value={addressState?.country ?? ''}
				onChange={e => formattedOnChange({ ...addressState, country: e.target.value })}
				onBlur={e => formattedOnBlur({ ...addressState, country: e.target.value })}
				renderValue={variantName === 'placeholders' ? v => (countryCodeToNameJsonAll ?? {})[v] : undefined}
				{...fieldPropsMerged}
				{...selectPropsMerged}
			>
				<SelectDropdownHeading>Common</SelectDropdownHeading>
				{countryCodeToNameJsonOther
					? Object.keys(countryCodeToNameJsonTop ?? {})?.map((c, idx) => (
							<MuiMenuItem key={`top${c}`} value={c}>
								{TOP_COUNTRIES[c]}
							</MuiMenuItem>
						))
					: ''}
				<Divider />
				<SelectDropdownHeading>Others</SelectDropdownHeading>
				{Object.keys(countryCodeToNameJsonOther ?? {}).map((c, idx) => (
					<MuiMenuItem key={idx} value={c}>
						{countryCodeToNameJsonAll?.[c] ?? ''}
					</MuiMenuItem>
				))}
			</StyledLabeledSelect>
		</GridWrapper>
	);
}
